import React, { useState, useRef, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { isWhiteLabel } from "../services/agencyService";
import Papa from 'papaparse';
import { uploadSubscribers } from "../services/subscribersService";
import customFieldsService from "../services/customFieldsService";
import { CustomFieldDefinition } from "../types/customFields";
import { renderInput } from "./CustomFieldInput";
import InlineFieldCreator from "./InlineFieldCreator";

interface FieldAssignment {
  definitionId: string;
  value: string | number | boolean | null;
}

export interface SubscriberImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const SubscriberImportModal: React.FC<SubscriberImportModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [monitoringUnderstood, setMonitoringUnderstood] = useState(false);
  const [skipWelcomeEmail, setSkipWelcomeEmail] = useState(false);
  const [description, setDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Custom fields state
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [blanketAssignments, setBlanketAssignments] = useState<FieldAssignment[]>([]);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [showAddField, setShowAddField] = useState(false);

  useEffect(() => {
    customFieldsService.listDefinitions().then(setDefinitions).catch(console.error);
  }, []);

  const findEmailColumn = (columns: string[] | undefined): string | null => {
    if (!columns) return null;

    const emailVariations = [
      'email',
      'email address',
      'email_address',
      'e-mail',
      'e_mail',
      'emailaddress'
    ];

    // Find the first matching column (case-insensitive)
    for (const variation of emailVariations) {
      const found = columns.find(col =>
        col.toLowerCase().trim() === variation
      );
      if (found) return found;
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setErrorMessage('Please upload a CSV file');
      return;
    }

    if (file.size === 0) {
      setErrorMessage('File appears to be empty');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setErrorMessage('File must be under 5MB');
      return;
    }

    setSelectedFile(file);
    setErrorMessage('');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const columns = results.meta.fields;
        const emailKey = findEmailColumn(columns);

        if (!emailKey) {
          setErrorMessage('CSV must contain an email column (e.g., "email", "email address", "email_address")');
          setSelectedFile(null);
          setParsedData([]);
          setCsvColumns([]);
          return;
        }

        // Validate email format for first few rows as preview
        const validEmails = results.data.filter((row: any) => {
          const email = row[emailKey];
          return email && email.includes('@');
        });

        // Check if list is too large
        if (validEmails.length > 1000) {
          setErrorMessage('Lists over 1000 contacts require manual review. Please send your list to team@withpotions.com');
          setSelectedFile(null);
          setParsedData([]);
          setCsvColumns([]);
          return;
        }

        setParsedData(validEmails.slice(0, 5)); // Show first 5 for preview

        // Populate non-email columns for mapping
        const nonEmailColumns = (columns || []).filter(col => col !== emailKey);
        setCsvColumns(nonEmailColumns);
        // Reset mappings when a new file is selected
        setColumnMappings({});
      },
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !consentConfirmed || !monitoringUnderstood) return;

    setIsUploading(true);
    setUploadProgress(50); // Show progress during upload

    try {
      const emailCount = parsedData.length > 0 ? parsedData.length : undefined;

      // Build field metadata
      const blanketObj: Record<string, string | number | boolean | null> = {};
      for (const a of blanketAssignments) {
        if (a.definitionId && a.value !== null) {
          blanketObj[a.definitionId] = a.value;
        }
      }
      const filteredMappings: Record<string, string> = {};
      for (const [col, defId] of Object.entries(columnMappings)) {
        if (defId) filteredMappings[col] = defId;
      }

      const hasMetadata = Object.keys(blanketObj).length > 0 || Object.keys(filteredMappings).length > 0;
      const fieldMetadata = hasMetadata
        ? JSON.stringify({ blanket_assignments: blanketObj, column_mappings: filteredMappings })
        : undefined;

      await uploadSubscribers(selectedFile, description.trim() || undefined, emailCount, fieldMetadata, skipWelcomeEmail);

      setUploadProgress(100);
      setIsUploading(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      setIsUploading(false);
      setUploadProgress(0);
      setErrorMessage(error.response?.data?.error || 'Upload failed. Please try again.');
    }
  };

  // Blanket assignment helpers
  const addBlanketAssignment = () => {
    setBlanketAssignments([...blanketAssignments, { definitionId: "", value: null }]);
  };

  const removeBlanketAssignment = (index: number) => {
    setBlanketAssignments(blanketAssignments.filter((_, i) => i !== index));
  };

  const updateBlanketAssignment = (index: number, update: Partial<FieldAssignment>) => {
    setBlanketAssignments(blanketAssignments.map((a, i) => {
      if (i !== index) return a;
      const updated = { ...a, ...update };
      if (update.definitionId !== undefined && update.definitionId !== a.definitionId) {
        updated.value = null;
      }
      return updated;
    }));
  };

  const usedBlanketIds = new Set(blanketAssignments.map((a) => a.definitionId).filter(Boolean));

  const handleFieldCreated = (def: CustomFieldDefinition) => {
    setDefinitions((prev) => [...prev, def]);
    setShowAddField(false);
  };

  return (
    <div
      className="primary-input-container"
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: "32px",
        maxWidth: "500px",
        width: "90%",
        zIndex: 1001,
        maxHeight: "90vh",
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h2>Import Subscriber List</h2>

      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}>
        {/* File Upload Area */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{
            border: "2px dashed #D1D5DB",
            borderRadius: "8px",
            padding: "32px",
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: selectedFile ? "#F3F4F6" : "#FAFAFA",
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />

          {selectedFile ? (
            <div>
              <p style={{ margin: "0 0 8px 0", fontWeight: "bold" }}>✓ {selectedFile.name}</p>
              <p style={{ margin: "0", fontSize: "14px", color: "#6B7280" }}>
                Click to select a different file
              </p>
            </div>
          ) : (
            <div>
              <p style={{ margin: "0 0 8px 0", fontSize: "16px" }}>Drop your CSV file here</p>
              <p style={{ margin: "0", fontSize: "14px", color: "#6B7280" }}>
                or click to browse
              </p>
            </div>
          )}
        </div>

        {/* Preview */}
        {parsedData.length > 0 && (
          <div style={{
            backgroundColor: "#F9FAFB",
            borderRadius: "6px",
            padding: "12px",
          }}>
            <strong>Preview (first 5 rows):</strong>
            <div style={{ margin: "8px 0 0 0", fontSize: "14px", fontFamily: "monospace" }}>
              {parsedData.map((row: any, index: number) => (
                <div key={index} style={{ margin: "4px 0" }}>{row.email || row.Email}</div>
              ))}
            </div>
          </div>
        )}

        {/* File Format Instructions */}
        <div style={{
          backgroundColor: "#F3F4F6",
          borderRadius: "6px",
          padding: "12px",
          fontSize: "14px",
        }}>
          <strong>CSV Format Requirements:</strong>
          <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
            <li>Must contain an email column</li>
            <li>One email address per row</li>
          </ul>
        </div>

        {/* Description Input */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <input
            type="text"
            placeholder="How did you source this list?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              width: "95%",
              padding: "8px 12px",
              border: "1px solid #D1D5DB",
              borderRadius: "6px",
              fontSize: "14px",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => e.target.style.borderColor = "#5D5DED"}
            onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
          />
        </div>

        {/* Custom Fields: Blanket Assignment */}
        {selectedFile && (
          <div style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "16px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <strong style={{ fontSize: "14px" }}>Apply field to all subscribers</strong>
            </div>

            {blanketAssignments.map((assignment, index) => {
              const selectedDef = definitions.find((d) => String(d.id) === assignment.definitionId);
              return (
                <div key={index} style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "8px" }}>
                  <select
                    value={assignment.definitionId}
                    onChange={(e) => updateBlanketAssignment(index, { definitionId: e.target.value })}
                    style={{
                      flex: 1,
                      padding: "6px 10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="">Select field...</option>
                    {definitions
                      .filter((d) => String(d.id) === assignment.definitionId || !usedBlanketIds.has(String(d.id)))
                      .map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name} ({d.field_type})
                        </option>
                      ))}
                  </select>
                  {selectedDef && (
                    <div style={{ flex: 1 }}>
                      {renderInput(selectedDef, assignment.value, (val) =>
                        updateBlanketAssignment(index, { value: val })
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => removeBlanketAssignment(index)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#9ca3af",
                      padding: "6px",
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              );
            })}

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={addBlanketAssignment}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6b7280",
                  fontSize: "13px",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Plus size={12} /> Add field
              </button>
              <button
                onClick={() => setShowAddField(true)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6b7280",
                  fontSize: "13px",
                  padding: "0",
                }}
              >
                + Create new field
              </button>
            </div>

            {showAddField && (
              <div style={{ marginTop: "8px" }}>
                <InlineFieldCreator
                  onFieldCreated={handleFieldCreated}
                  onCancel={() => setShowAddField(false)}
                />
              </div>
            )}
          </div>
        )}

        {/* Column Mapping */}
        {csvColumns.length > 0 && (
          <div style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "16px",
          }}>
            <strong style={{ fontSize: "14px", display: "block", marginBottom: "8px" }}>
              Map CSV columns to custom fields
            </strong>
            <p style={{ fontSize: "13px", color: "#6b7280", margin: "0 0 12px 0" }}>
              Your CSV has additional columns. Map them to custom fields or skip.
            </p>

            {csvColumns.map((col) => (
              <div key={col} style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                <span style={{
                  flex: 1,
                  fontSize: "14px",
                  fontFamily: "monospace",
                  color: "#374151",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {col}
                </span>
                <select
                  value={columnMappings[col] || ""}
                  onChange={(e) => setColumnMappings({ ...columnMappings, [col]: e.target.value })}
                  style={{
                    flex: 1,
                    padding: "6px 10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Skip</option>
                  {definitions.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.name} ({d.field_type})
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {/* Consent Checkboxes */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <input
              type="checkbox"
              id="consent-checkbox"
              checked={consentConfirmed}
              onChange={(e) => setConsentConfirmed(e.target.checked)}
              style={{ marginTop: "2px" }}
            />
            <label htmlFor="consent-checkbox" style={{ fontSize: "14px" }}>
              I confirm that all emails on this list have explicitly opted in to receiving marketing communications from me
            </label>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <input
              type="checkbox"
              id="monitoring-checkbox"
              checked={monitoringUnderstood}
              onChange={(e) => setMonitoringUnderstood(e.target.checked)}
              style={{ marginTop: "2px" }}
            />
            <label htmlFor="monitoring-checkbox" style={{ fontSize: "14px" }}>
              I understand that {isWhiteLabel() ? "the platform" : "Potions"} may pause sending after a spike in unsubscribes or spam complaints to protect deliverability for all customers
            </label>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <input
              type="checkbox"
              id="skip-welcome-email-checkbox"
              checked={skipWelcomeEmail}
              onChange={(e) => setSkipWelcomeEmail(e.target.checked)}
              style={{ marginTop: "2px" }}
            />
            <label htmlFor="skip-welcome-email-checkbox" style={{ fontSize: "14px" }}>
              Skip welcome email for this list ⓘ
              <span style={{ display: "block", color: "#6b7280", fontSize: "12px", marginTop: "2px" }}>
                This list is reviewed and imported by hand — this just flags your request for whoever processes it, it isn't applied automatically.
              </span>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div style={{ color: "#DC2626", fontSize: "14px", textAlign: "center" }}>
            {errorMessage}
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div>
            <div style={{ fontSize: "14px", marginBottom: "8px" }}>
              Uploading... {uploadProgress}%
            </div>
            <div style={{
              width: "100%",
              backgroundColor: "#E5E7EB",
              borderRadius: "4px",
              height: "8px",
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                backgroundColor: "#10B981",
                height: "100%",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }} />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <button
            className="primary-button"
            onClick={handleUpload}
            disabled={!selectedFile || !consentConfirmed || !monitoringUnderstood || isUploading}
          >
            {isUploading ? "Uploading..." : "Import Subscribers"}
          </button>
          <button
            className="tertiary-button"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriberImportModal;
